import { styled } from "@mui/material";

import type { ShapeIconOwnerState } from "./index";

const largeOutlineStyle = {
    "::before": {
        width: "calc(100% + 12px)",
        height: "calc(100% + 12px)",
        top: "-6px",
        left: "-6px",
        borderWidth: 3,
    },
    "::after": {
        width: "calc(100% + 24px)",
        height: "calc(100% + 24px)",
        top: "-12px",
        left: "-12px",
        borderWidth: 3,
    },
};

export const ShapeIconRoot: React.ElementType = styled("div", {
    name: "MuiShapeIcon",
    slot: "root",
})<{ ownerState: ShapeIconOwnerState }>(({ theme }) => ({
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    variants: [
        {
            props: { size: "2xsmall" },
            style: {
                width: "20px",
                height: "20px",
                p: 0.5,
                fontSize: "12px",
            },
        },
        {
            props: { size: "xsmall" },
            style: {
                width: "24px",
                height: "24px",
                p: 0.75,
                fontSize: "12px",
            },
        },
        {
            props: { size: "small" },
            style: {
                width: "32px",
                height: "32px",
                p: 1,
                fontSize: "16px",
            },
        },
        {
            props: { size: "medium" },
            style: {
                width: "40px",
                height: "40px",
                p: 1.25,
                fontSize: "20px",
            },
        },
        {
            props: { size: "large" },
            style: {
                width: "48px",
                height: "48px",
                p: 1.5,
                fontSize: "24px",
            },
        },
        {
            props: { size: "xlarge" },
            style: {
                width: "64px",
                height: "64px",
                p: 2,
                fontSize: "32px",
            },
        },
        {
            props: { color: "gray" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeGrayA,
                color: theme.palette.tokens?.icon.iconGray,
            },
        },
        {
            props: { color: "accent" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeAccentA,
                color: theme.palette.tokens?.icon.iconAccent,
            },
        },
        {
            props: { color: "error" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeErrorA,
                color: theme.palette.tokens?.icon.iconError,
            },
        },
        {
            props: { color: "warning" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeWarningA,
                color: theme.palette.tokens?.icon.iconWarning,
            },
        },
        {
            props: { color: "success" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeSuccessA,
                color: theme.palette.tokens?.icon.iconSuccess,
            },
        },
        {
            props: { color: "oceanGreen" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeOceanGreenA,
                color: theme.palette.tokens?.icon.iconOceanGreen,
            },
        },
        {
            props: { color: "indigo" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeIndigoA,
                color: theme.palette.tokens?.icon.iconIndigo,
            },
        },
        {
            props: { color: "purple" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapePurpleA,
                color: theme.palette.tokens?.icon.iconPurple,
            },
        },
        // ======================== Contrast variant ========================
        {
            props: { color: "gray", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeGrayB,
                color: "white",
            },
        },
        {
            props: { color: "accent", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeAccentB,
                color: "white",
            },
        },
        {
            props: { color: "error", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeErrorB,
                color: "white",
            },
        },
        {
            props: { color: "warning", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeWarningB,
                color: "white",
            },
        },
        {
            props: { color: "success", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeSuccessB,
                color: "white",
            },
        },
        {
            props: { color: "oceanGreen", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeOceanGreenB,
                color: "white",
            },
        },
        {
            props: { color: "indigo", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapeIndigoB,
                color: "white",
            },
        },
        {
            props: { color: "purple", variant: "contrast" },
            style: {
                backgroundColor: theme.palette.tokens?.shapeIcon.shapePurpleB,
                color: "white",
            },
        },
        // ======================== Contrast outline ========================
        {
            props: { variant: "outline" },
            style: {
                position: "relative",
                "::before": {
                    content: '""',
                    position: "absolute",
                    width: "calc(100% + 8px)",
                    height: "calc(100% + 8px)",
                    top: "-4px",
                    left: "-4px",
                    borderRadius: "100%",
                    borderWidth: 2,
                    borderStyle: "solid",
                },
                "::after": {
                    content: '""',
                    position: "absolute",
                    width: "calc(100% + 16px)",
                    height: "calc(100% + 16px)",
                    top: "-8px",
                    left: "-8px",
                    borderRadius: "100%",
                    borderWidth: 2,
                    borderStyle: "solid",
                },
            },
        },
        {
            props: { variant: "outline", size: "lg" },
            style: largeOutlineStyle,
        },
        {
            props: { variant: "outline", size: "xl" },
            style: largeOutlineStyle,
        },
        {
            props: { color: "gray", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.gray02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.gray01,
                },
            },
        },
        {
            props: { color: "accent", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.accent02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.accent01,
                },
            },
        },
        {
            props: { color: "error", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.error02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.error01,
                },
            },
        },
        {
            props: { color: "warning", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.warning02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.warning01,
                },
            },
        },
        {
            props: { color: "success", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.success02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.success01,
                },
            },
        },
        {
            props: { color: "oceanGreen", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.oceanGreen02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.oceanGreen01,
                },
            },
        },
        {
            props: { color: "indigo", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.indigo02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.indigo01,
                },
            },
        },
        {
            props: { color: "purple", variant: "outline" },
            style: {
                "::before": {
                    borderColor: theme.palette.tokens?.alphaTokens.purple02,
                },
                "::after": {
                    borderColor: theme.palette.tokens?.alphaTokens.purple01,
                },
            },
        },
    ],
}));
